import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
    Activity, 
    Stethoscope, 
    Clock, 
    Shield, 
    ArrowRight,
    Heart,
    Brain,
    Pill,
    AlertTriangle,
    CheckCircle2,
    Download,
    Smartphone,
    Share2,
    Copy,
    X
} from 'lucide-react';
import { toast } from 'sonner';

export default function Landing() {
    const { t } = useLanguage();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    
    // Calculate initial states
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Always show install/share buttons (share is always useful)
    const [showInstallButton, setShowInstallButton] = useState(true);
    const [isIOS] = useState(isIOSDevice);
    const [showInstallModal, setShowInstallModal] = useState(false);

    useEffect(() => {
        // Hide install button if already installed as PWA
        if (isInstalled) {
            setShowInstallButton(false);
            return;
        }

        // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, [isInstalled]);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            // Native install prompt available
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                toast.success('App installed successfully!');
                setShowInstallButton(false);
            }
            setDeferredPrompt(null);
        } else {
            // Show install instructions modal
            setShowInstallModal(true);
        }
    };

    const handleShareApp = async () => {
        const shareData = {
            title: 'MediGuide - AI Health Assessment',
            text: 'Get instant AI-powered symptom analysis and health recommendations. Free, private, and secure.',
            url: window.location.origin
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
                toast.success('Thanks for sharing!');
            } else {
                // Fallback: copy to clipboard
                await navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`);
                toast.success('Link copied to clipboard!');
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                // User didn't cancel - try clipboard fallback
                try {
                    await navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`);
                    toast.success('Link copied to clipboard!');
                } catch {
                    // Final fallback - show the URL in a toast that can be copied
                    toast.info(
                        <div className="space-y-2">
                            <p className="font-medium">Share MediGuide</p>
                            <p className="text-xs break-all bg-slate-100 dark:bg-slate-800 p-2 rounded">{window.location.origin}</p>
                            <p className="text-xs text-muted-foreground">Copy this link to share with friends</p>
                        </div>,
                        { duration: 8000 }
                    );
                }
            }
        }
    };
    const features = [
        {
            icon: Brain,
            title: t('aiPowered'),
            description: t('aiPoweredDesc')
        },
        {
            icon: AlertTriangle,
            title: t('urgencyAssessment') || 'Urgency Assessment',
            description: t('knowImmediately') || 'Know immediately if you need emergency care, urgent attention, or routine care.'
        },
        {
            icon: Pill,
            title: t('treatmentGuidance') || 'Treatment Guidance',
            description: t('getFirstAidTips') || 'Get first aid tips, treatment options, and investigation recommendations.'
        },
        {
            icon: Clock,
            title: t('instantResults'),
            description: t('instantResultsDesc')
        }
    ];

    const steps = [
        { step: '01', title: t('selectBodyArea') || 'Select Body Area', description: t('chooseAffectedParts') || 'Choose affected body parts' },
        { step: '02', title: t('describeSymptoms'), description: t('detailWhatExperiencing') || 'Detail what you\'re experiencing' },
        { step: '03', title: t('getAssessment') || 'Get Assessment', description: t('receiveAiAssessment') || 'Receive AI-powered assessment' }
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="hero-gradient relative overflow-hidden" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-24">
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        {/* Text Content */}
                        <div className="lg:col-span-7 space-y-5">
                            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                                <Activity className="w-4 h-4" />
                                {t('heroTitle')}
                            </div>
                            
                            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-tight text-foreground">
                                {t('heroSubtitle')}
                            </h1>
                            
                            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                                {t('heroSubtitle')}
                            </p>
                            
                            {/* Main CTAs - ALWAYS on same line */}
                            <div className="flex flex-row gap-2 sm:gap-3">
                                <Link to="/register" className="flex-1 sm:flex-none">
                                    <Button 
                                        size="default" 
                                        className="w-full sm:w-auto rounded-full px-4 sm:px-8 shadow-lg shadow-primary/25 group text-sm sm:text-base"
                                        data-testid="get-started-btn"
                                    >
                                        {t('startAssessment')}
                                        <ArrowRight className="w-4 h-4 ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </Link>
                                <Link to="/login" className="flex-shrink-0">
                                    <Button 
                                        size="default" 
                                        variant="outline" 
                                        className="rounded-full px-4 sm:px-8 text-sm sm:text-base"
                                        data-testid="login-hero-btn"
                                    >
                                        {t('signIn')}
                                    </Button>
                                </Link>
                            </div>

                            {/* Trust indicators */}
                            <div className="flex items-center gap-4 sm:gap-6">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Shield className="w-4 h-4 text-success" />
                                    {t('securePrivate')}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <CheckCircle2 className="w-4 h-4 text-success" />
                                    {t('freeTrial')}
                                </div>
                            </div>

                            {/* GLOWING Install App & Share Buttons - Prominent placement */}
                            {showInstallButton && !isInstalled && (
                                <div className="relative py-3 sm:py-4">
                                    {/* Animated glow effect background */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-accent to-primary rounded-2xl blur-lg opacity-60 animate-pulse" />
                                    <div className="absolute inset-0 bg-gradient-to-r from-accent via-primary to-accent rounded-2xl blur-xl opacity-40 animate-pulse" style={{animationDelay: '0.5s'}} />
                                    <div className="relative bg-white/90 dark:bg-slate-900/90 backdrop-blur-md rounded-2xl p-4 sm:p-5 border-2 border-primary/40 shadow-2xl">
                                        <p className="text-center text-sm font-semibold text-foreground mb-3">
                                            📱 {t('getBestExperience') || 'Get the best experience'}
                                        </p>
                                        <div className="flex flex-row justify-center gap-2 sm:gap-3">
                                            <Button
                                                onClick={handleInstallClick}
                                                className="rounded-full px-4 sm:px-6 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-lg text-sm sm:text-base font-semibold"
                                                data-testid="install-app-btn"
                                            >
                                                <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                                {isIOS ? t('addToHome') || 'Add to Home' : t('installApp')}
                                            </Button>
                                            <Button
                                                onClick={handleShareApp}
                                                variant="outline"
                                                className="rounded-full px-4 sm:px-6 border-2 border-primary text-primary hover:bg-primary/10 text-sm sm:text-base font-semibold"
                                                data-testid="share-app-btn"
                                            >
                                                <Share2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                                {t('share')}
                                            </Button>
                                        </div>
                                        <p className="text-center text-xs text-muted-foreground mt-2">
                                            {t('worksOffline') || 'Works offline • Quick access • Free forever'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* MediProvider CTA */}
                            <div className="text-center py-3 border-t border-slate-200 dark:border-slate-700">
                                <Link to="/mediprovider/register">
                                    <Button 
                                        size="lg"
                                        variant="outline" 
                                        className="rounded-full px-6 sm:px-8 border-teal-500 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-950/30"
                                        data-testid="mediprovider-signup-btn"
                                    >
                                        <Stethoscope className="w-5 h-5 mr-2" />
                                        {t('joinAsProvider') || 'Join as Healthcare Provider'}
                                    </Button>
                                </Link>
                                <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                                    {t('earnByProvidingCare') || 'Doctors, nurses & healthcare professionals — earn by providing care'}
                                </p>
                            </div>
                        </div>

                        {/* Hero Image */}
                        <div className="lg:col-span-5 relative">
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl" />
                                <img
                                    src="https://images.pexels.com/photos/8376277/pexels-photo-8376277.jpeg"
                                    alt="Healthcare consultation"
                                    className="relative rounded-3xl shadow-2xl object-cover w-full h-[300px] sm:h-[400px] lg:h-[500px]"
                                    data-testid="hero-image"
                                />
                                {/* Floating card */}
                                <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 glass-card rounded-2xl p-3 sm:p-4 shadow-xl animate-fadeIn">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-success/20 flex items-center justify-center">
                                            <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-foreground">{t('quickResults') || 'Quick Results'}</p>
                                            <p className="text-sm text-muted-foreground">{t('underSeconds') || 'Under 30 seconds'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 lg:py-32 bg-card" data-testid="features-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
                            {t('comprehensiveHealthInsights') || 'Comprehensive Health Insights'}
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            {t('ourAiAnalyzes') || 'Our AI analyzes your symptoms to provide detailed health assessments and actionable recommendations.'}
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <Card 
                                key={feature.title} 
                                className="card-lift border-slate-200 dark:border-slate-700"
                                style={{ animationDelay: `${index * 0.1}s` }}
                                data-testid={`feature-card-${index}`}
                            >
                                <CardContent className="p-6 space-y-4">
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="font-heading font-semibold text-lg">{feature.title}</h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed">
                                        {feature.description}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-20 lg:py-32" data-testid="how-it-works-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-6">
                                {t('howItWorks')}
                            </h2>
                            <p className="text-muted-foreground text-lg mb-12">
                                {t('threeSimpleSteps') || 'Three simple steps to understand your symptoms and get the guidance you need.'}
                            </p>

                            <div className="space-y-8">
                                {steps.map((item, index) => (
                                    <div 
                                        key={item.step} 
                                        className="flex items-start gap-4"
                                        data-testid={`step-${index + 1}`}
                                    >
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-heading font-bold text-sm">
                                            {item.step}
                                        </div>
                                        <div>
                                            <h3 className="font-heading font-semibold text-lg mb-1">
                                                {item.title}
                                            </h3>
                                            <p className="text-muted-foreground">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-12">
                                <Link to="/register">
                                    <Button 
                                        size="lg" 
                                        className="rounded-full px-8 shadow-lg shadow-primary/25"
                                        data-testid="try-now-btn"
                                    >
                                        {t('tryItNowFree') || 'Try It Now — Free'}
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        <div className="relative">
                            <img
                                src="https://images.pexels.com/photos/6753469/pexels-photo-6753469.jpeg"
                                alt="First aid and medical care"
                                className="rounded-3xl shadow-xl object-cover w-full h-[500px]"
                                data-testid="how-it-works-image"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Install App Section */}
            {showInstallButton && !isInstalled && (
                <section className="py-16 bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-950/30 dark:to-blue-950/30" data-testid="install-section">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary-600 flex items-center justify-center shadow-lg">
                                    <Smartphone className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-heading text-xl font-semibold mb-1">
                                        {t('getMediGuideOnDevice') || 'Get MediGuide on Your Device'}
                                    </h3>
                                    <p className="text-muted-foreground">
                                        {t('installForInstantAccess') || 'Install our app for instant access — works offline, no app store needed!'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button
                                    onClick={handleInstallClick}
                                    size="lg"
                                    className="rounded-full px-8 shadow-lg shadow-primary/25 whitespace-nowrap"
                                    data-testid="install-app-section-btn"
                                >
                                    <Download className="w-5 h-5 mr-2" />
                                    {isIOS ? t('addToHomeScreen') || 'Add to Home Screen' : t('installFreeApp') || 'Install Free App'}
                                </Button>
                                <Button
                                    onClick={handleShareApp}
                                    variant="outline"
                                    size="lg"
                                    className="rounded-full px-6 whitespace-nowrap"
                                    data-testid="share-app-section-btn"
                                >
                                    <Share2 className="w-5 h-5 mr-2" />
                                    {t('shareApp')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700" data-testid="cta-section">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                            <Stethoscope className="w-8 h-8 text-white" />
                        </div>
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                        {t('readyToUnderstand') || 'Ready to Understand Your Symptoms?'}
                    </h2>
                    <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
                        {t('joinThousands') || 'Join thousands who use MediGuide to make informed health decisions. It\'s free, private, and takes less than 2 minutes.'}
                    </p>
                    <Link to="/register">
                        <Button 
                            size="lg" 
                            variant="secondary" 
                            className="rounded-full px-8 bg-white text-primary-600 hover:bg-white/90"
                            data-testid="cta-btn"
                        >
                            {t('getStartedFree')}
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Install Instructions Modal */}
            {showInstallModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <Download className="w-5 h-5 text-primary" />
                                {t('installMediGuide') || 'Install MediGuide'}
                            </h3>
                            <button 
                                onClick={() => setShowInstallModal(false)}
                                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {isIOS ? (
                            <div className="space-y-4">
                                {/* iOS Important Notice */}
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                                    <p className="text-amber-800 dark:text-amber-200 text-sm font-medium flex items-start gap-2">
                                        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                        <span>Apple requires iPhone/iPad users to install apps manually using Safari. Follow these 3 simple steps:</span>
                                    </p>
                                </div>

                                {/* Step-by-step iOS instructions with visuals */}
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800">
                                        <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                                        <div>
                                            <p className="font-semibold text-foreground">Tap the Share button</p>
                                            <p className="text-sm text-muted-foreground mt-1">Look for this icon at the bottom of Safari:</p>
                                            <div className="mt-2 inline-flex items-center justify-center w-10 h-10 bg-white dark:bg-slate-800 rounded-lg border shadow-sm">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-blue-600">
                                                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                                                    <polyline points="16 6 12 2 8 6"/>
                                                    <line x1="12" y1="2" x2="12" y2="15"/>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border-2 border-green-200 dark:border-green-800">
                                        <span className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                                        <div>
                                            <p className="font-semibold text-foreground">Scroll down & tap "Add to Home Screen"</p>
                                            <p className="text-sm text-muted-foreground mt-1">Scroll through the share menu options until you see:</p>
                                            <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border shadow-sm">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-green-600">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                    <line x1="12" y1="8" x2="12" y2="16"/>
                                                    <line x1="8" y1="12" x2="16" y2="12"/>
                                                </svg>
                                                <span className="text-sm font-medium">Add to Home Screen</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border-2 border-purple-200 dark:border-purple-800">
                                        <span className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                                        <div>
                                            <p className="font-semibold text-foreground">Tap "Add" to confirm</p>
                                            <p className="text-sm text-muted-foreground mt-1">MediGuide will appear on your home screen like a regular app!</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Not using Safari warning */}
                                <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4 text-center">
                                    <p className="text-sm text-muted-foreground">
                                        <strong>Not using Safari?</strong> Open this page in Safari to install.
                                    </p>
                                    <Button 
                                        variant="link" 
                                        className="text-primary p-0 h-auto mt-1"
                                        onClick={() => {
                                            navigator.clipboard.writeText(window.location.origin);
                                            toast.success('Link copied! Paste it in Safari');
                                        }}
                                    >
                                        <Copy className="w-4 h-4 mr-1" />
                                        Copy link for Safari
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <p className="text-muted-foreground mb-4">
                                    {t('installForQuickAccess') || 'Install MediGuide on your device for quick access, offline support, and a native app experience.'}
                                </p>
                                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                    <p className="font-medium text-sm mb-2">{t('forChrome') || 'For Chrome / Edge:'}</p>
                                    <p className="text-sm text-muted-foreground">{t('lookForInstallIcon') || 'Look for the install icon in the address bar, or click the menu (⋮) and select "Install app"'}</p>
                                </div>
                                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                    <p className="font-medium text-sm mb-2">{t('forFirefox') || 'For Firefox:'}</p>
                                    <p className="text-sm text-muted-foreground">{t('firefoxInstructions') || 'Click the menu → More tools → Create shortcut → Check "Open as window"'}</p>
                                </div>
                                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                    <p className="font-medium text-sm mb-2">{t('forSafariMac') || 'For Safari (Mac):'}</p>
                                    <p className="text-sm text-muted-foreground">{t('safariMacInstructions') || 'Click File → Add to Dock'}</p>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex gap-3">
                            <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={() => setShowInstallModal(false)}
                            >
                                {t('maybeLater') || 'Maybe Later'}
                            </Button>
                            <Button 
                                className="flex-1"
                                onClick={() => {
                                    navigator.clipboard.writeText(window.location.origin);
                                    toast.success(t('linkCopied') || 'URL copied! Share with friends');
                                    setShowInstallModal(false);
                                }}
                            >
                                <Copy className="w-4 h-4 mr-2" />
                                {t('copyLink') || 'Copy Link'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer with Legal Links */}
            <footer className="bg-card border-t border-slate-200 dark:border-slate-800 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Activity className="w-5 h-5 text-primary" />
                            <span className="font-heading font-semibold">MediGuide AI</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                            <Link to="/privacy" className="hover:text-primary transition-colors">
                                {t('privacyPolicy') || 'Privacy Policy'}
                            </Link>
                            <Link to="/terms" className="hover:text-primary transition-colors">
                                {t('termsOfService') || 'Terms of Service'}
                            </Link>
                            <a href="mailto:support@mediguide.online" className="hover:text-primary transition-colors">
                                {t('contact') || 'Contact'}
                            </a>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            © {new Date().getFullYear()} MediGuide AI. {t('allRightsReserved') || 'All rights reserved.'}
                        </p>
                    </div>
                    <p className="text-center text-xs text-muted-foreground mt-4">
                        {t('medicalDisclaimer') || 'MediGuide AI is for informational purposes only and does not replace professional medical advice.'}
                    </p>
                </div>
            </footer>
        </div>
    );
}
